/**
 * occupancyApi.ts
 * ---------------
 * Types and fetch helpers for the occupancy analysis endpoint.
 * Place at: src/analysis/occupancyApi.ts
 */


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CarriageData {
  carriage_index: number;
  vehicle_number: string;
  belegung: number | null;
  kapazitaet: number | null;
  auslastungsstufe: string | null;
}

export interface UicData {
  uic: string;
  unit_id: string | null;
  unit_index: number | null;
  sensor_source: string | null;   // 'AFZ' | 'WIFI' | null
  belegung: number | null;
  kapazitaet: number | null;
  auslastungsstufe: string | null;
  carriages: CarriageData[];
}

export interface PrognosePoint {
  target_seq: number;
  target_stop_eva: string;
  uics: UicData[];
  total_belegung: number | null;
}

export interface OccupancyStop {
  stop_sequence: number;
  stop_place_eva: string;
  stop_name: string | null;
  scheduled_departure_time: string | null;
  actual_departure_time: string | null;
  completed: boolean;
  stop_cancelled: boolean;
  vubasis_sent: string | null;
  type_message_output: string | null;   // 'FORECAST' | 'AFZ' | 'WIFI' | null
  model_version: string | null;
  pareto_version: string | null;
  ist_belegung: number | null;
  ist_kapazitaet: number | null;
  ist_auslastungsstufe: string | null;
  ist_uics: UicData[];
  prognose: PrognosePoint[];
}

export interface OccupancyResponse {
  journey_id: string;
  stops: OccupancyStop[];
}

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

// Remove this import entirely:
// import { getActiveEnv } from '../EnvContext';

// Add these at the top of the file alongside the other constants:
const BASE_URL = (process.env.REACT_APP_ANALYSIS_API_URL ?? '').replace(/\/$/, '');
const API_KEY  = process.env.REACT_APP_ANALYSIS_API_KEY ?? '';

export async function fetchOccupancy(journeyId: string): Promise<OccupancyResponse> {
  const url = `${BASE_URL}/api/v1/journeys/${encodeURIComponent(journeyId)}/occupancy`;

  const res = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...(API_KEY ? { 'x-api-key': API_KEY } : {}),
    },
  });

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = body.detail;
    } catch { /* ignore */ }
    throw new Error(`fetchOccupancy failed: ${detail}`);
  }

  return res.json() as Promise<OccupancyResponse>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function fmtDepTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('de-DE', {
      hour: '2-digit', minute: '2-digit',
      timeZone: 'Europe/Berlin',
    });
  } catch { return '—'; }
}

export function stopShortName(stop: OccupancyStop): string {
  const name = stop.stop_name ?? stop.stop_place_eva;
  return name.length > 16 ? name.slice(0, 15) + '…' : name;
}

/** Sum IST belegung across all UICs for a stop. */
export function istTotal(stop: OccupancyStop): number | null {
  if (stop.ist_belegung != null) return stop.ist_belegung;
  if (!stop.ist_uics.length) return null;
  const vals = stop.ist_uics.map(u => u.belegung ?? 0);
  return vals.reduce((a, b) => a + b, 0);
}

/** Colour palette for PROGNOSE series — one per origin stop. */
const PROGNOSE_COLOURS = [
  '#f0a500', '#38bdf8', '#4ade80', '#f472b6',
  '#a78bfa', '#fb923c', '#34d399', '#e879f9',
];
export function prognoseColour(stopIndex: number): string {
  return PROGNOSE_COLOURS[stopIndex % PROGNOSE_COLOURS.length];
}

/** Source badge colour */
export function sourceColour(source: string | null): string {
  if (source === 'AFZ')  return '#38bdf8';
  if (source === 'WIFI') return '#4ade80';
  return 'rgba(255,255,255,0.3)';
}